import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { LegalDocKey, legalConsentPayload } from 'src/app/shared/legal/legal-documents';

interface PendingLogin {
  token: string;
  userDataBase64: string;
  params: any;
}

@Component({
  selector: 'app-auth-callback-component',
  templateUrl: './auth-callback-component.component.html',
  styleUrl: './auth-callback-component.component.scss'
})
export class AuthCallbackComponentComponent implements OnInit {
  // Consent gate: shown when Google created a brand-new account without the
  // user having ticked the consent box first (e.g. "Continue with Google" on
  // the login page). The session is held here, NOT in localStorage, until
  // consent is recorded, so leaving the page does not leave a logged-in
  // account that never agreed to the Terms.
  showConsentGate = false;
  agreedToTerms = false;
  legalModal: LegalDocKey | null = null;
  isSubmitting = false;
  consentError = '';
  private pending: PendingLogin | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    // Extract query parameters from URL
    this.route.queryParams.subscribe(params => {
      const accessToken = params['access_token'];
      const userDataBase64 = params['user'];
      const error = params['error'];

      if (accessToken) {
        if (params['consent_required'] === '1') {
          this.pending = { token: accessToken, userDataBase64, params };
          this.showConsentGate = true;
          return;
        }
        // Process successful Google login
        this.processGoogleLogin(accessToken, userDataBase64, params);
      } else if (error) {
        // Handle error
        this.router.navigate(['/customers/login'], {
          queryParams: { error: error }
        });
      } else {
        // No parameters, redirect to login
        this.router.navigate(['/customers/login']);
      }
    });
  }

  agreeFromModal(): void {
    this.agreedToTerms = true;
    this.legalModal = null;
  }

  submitConsent(): void {
    if (!this.pending || !this.agreedToTerms || this.isSubmitting) return;
    this.isSubmitting = true;
    this.consentError = '';
    const { token, userDataBase64, params } = this.pending;
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http.post<any>(`${environment.apiUrl}customer/consent`, legalConsentPayload(), { headers })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.pending = null;
          this.processGoogleLogin(token, userDataBase64, params);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.consentError = err?.error?.message || 'Could not save your consent. Please try again.';
        },
      });
  }

  declineConsent(): void {
    this.pending = null;
    this.router.navigate(['/customers/login']);
  }

  private processGoogleLogin(token: string, userDataBase64: string, params: any): void {
    try {
      // Decode user data
      let user = null;
      if (userDataBase64) {
        user = JSON.parse(atob(userDataBase64));
      }

      const tokenType = params['token_type'] || 'bearer';
      const expiresIn = params['expires_in'] ? parseInt(params['expires_in']) : 86400;

      // Store authentication data
      localStorage.setItem('accessToken', token);
      localStorage.setItem('tokenType', tokenType);
      localStorage.setItem('tokenExpiry', (Date.now() + expiresIn * 1000).toString());
      localStorage.setItem('authType', 'google');

      if (user) {
        const userInfo = {
          id: user.external_id || user.id,
          email: user.email,
          name: user.name,
          'custom:team': user.team,
          'custom:visibility': user.user_type || 'customer',
          company: user.company || '',
          phone_number: user.customer_number || '',
          customer_region: user.customer_region || '',
          auth_type: 'google',
          picture: user.picture || ''
        };

        localStorage.setItem('userInfo', JSON.stringify(userInfo));
      }

      // Redirect to the customer console (Google OAuth is customer-side only).
      this.router.navigate(['/workspace/customer']);

    } catch (error) {
      console.error('Error processing Google callback:', error);
      this.router.navigate(['/customers/login'], {
        queryParams: { error: 'Failed to process authentication' }
      });
    }
  }

}
